import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, UserSquare, CheckCircle, RefreshCw, Clock,
  AlertCircle, ExternalLink, ChevronDown, Loader2, Video, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  in_progress: { cls: 'bg-amber-100 text-amber-700', label: 'Training...' },
  complete:    { cls: 'bg-green-100 text-green-700',  label: 'Ready' },
  failed:      { cls: 'bg-red-100 text-red-700',     label: 'Failed' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const ProgressBar = ({ progress }) => (
  <div className="w-full space-y-2">
    <div className="flex justify-between text-xs text-gray-500">
      <span>Submitting...</span><span>{progress}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
      <div
        className="h-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-1000"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const DigitalTwinPage = () => {
  const navigate = useNavigate();
  const [twins, setTwins] = useState([]);
  const [loadingTwins, setLoadingTwins] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'create'
  const [form, setForm] = useState({ training_footage_url: '', video_consent_url: '', avatar_name: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [newAvatarId, setNewAvatarId] = useState(null);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => { loadTwins(); }, []);

  const loadTwins = async () => {
    setLoadingTwins(true);
    const token = getToken();
    if (!token) { setLoadingTwins(false); return; }
    try {
      const res = await axios.get(`${API}/heygen/my-twins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data.twins || [];
      // Auto-poll in_progress ones
      const pending = list.filter(t => t.status === 'in_progress');
      const updated = [...list];
      for (const twin of pending) {
        try {
          const sr = await axios.get(`${API}/heygen/digital-twin-status/${twin.avatar_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const idx = updated.findIndex(t => t.avatar_id === twin.avatar_id);
          if (idx !== -1) updated[idx].status = sr.data.status;
        } catch (_) {}
      }
      setTwins(updated);
    } catch (_) {}
    setLoadingTwins(false);
  };

  const submit = async () => {
    const { training_footage_url, video_consent_url, avatar_name } = form;
    if (!training_footage_url.trim()) { toast.error('Enter training footage URL'); return; }
    if (!video_consent_url.trim()) { toast.error('Enter consent video URL'); return; }
    if (!avatar_name.trim()) { toast.error('Enter a name for your Digital Twin'); return; }

    const token = getToken();
    if (!token) { toast.error('Please sign in first'); return; }

    setStatus('submitting'); setProgress(20); setErrorMsg('');
    try {
      setProgress(50);
      const res = await axios.post(`${API}/heygen/digital-twin`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(100);
      setNewAvatarId(res.data.avatar_id);
      setStatus('done');
      toast.success('Digital Twin creation submitted! Training may take 30–60 minutes.');
      setForm({ training_footage_url: '', video_consent_url: '', avatar_name: '' });
      await loadTwins();
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.response?.data?.detail || 'Submission failed. Please check your footage URLs and try again.');
    }
  };

  const checkStatus = async (avatarId) => {
    const token = getToken();
    try {
      const res = await axios.get(`${API}/heygen/digital-twin-status/${avatarId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info(`Status: ${res.data.status}`);
      await loadTwins();
    } catch (e) {
      toast.error('Failed to check status');
    }
  };

  const useForVideo = (avatarId) => {
    navigate(`/creator-ai/avatar-video?avatar_id=${avatarId}`);
  };

  const reset = () => {
    setStatus('idle'); setProgress(0); setErrorMsg(''); setNewAvatarId(null);
    setView('list');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate('/creator-ai/heygen-hub')}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-1.5 text-xs sm:text-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <UserSquare className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">Digital Twin</h1>
                <p className="text-xs text-gray-500">Create your personalized AI avatar from training footage</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">

        {/* Enterprise notice */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-3 flex gap-3">
          <AlertCircle className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-indigo-800">Professional / Enterprise Feature</p>
            <p className="text-xs text-indigo-700 mt-0.5">
              Digital Twin creation requires high-quality training footage (2+ min, 720p+) and a consent video. Processing takes 30–60 minutes.
            </p>
          </div>
        </div>

        {/* Toggle buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => { setView('list'); reset(); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
              view === 'list' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700'
            }`}
          >
            My Twins ({twins.length})
          </button>
          <button
            onClick={() => setView('create')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
              view === 'create' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700'
            }`}
          >
            + Create New Twin
          </button>
        </div>

        {/* MY TWINS LIST */}
        {view === 'list' && (
          <>
            {loadingTwins ? (
              <div className="flex items-center gap-2 justify-center py-8 text-gray-400 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading your Digital Twins...
              </div>
            ) : twins.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <UserSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="font-semibold text-gray-600 mb-1">No Digital Twins yet</p>
                <p className="text-xs text-gray-400 mb-4">Create your first personalized AI avatar</p>
                <button
                  onClick={() => setView('create')}
                  className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Create Your First Twin
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {twins.map(twin => {
                  const st = STATUS_CONFIG[twin.status] || STATUS_CONFIG.in_progress;
                  return (
                    <div key={twin.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                            <UserSquare className="h-5 w-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{twin.avatar_name}</p>
                            <p className="text-xs text-gray-400">{fmtDate(twin.created_at)}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>
                          {twin.status === 'in_progress' && <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{st.label}</span>}
                          {twin.status !== 'in_progress' && st.label}
                        </span>
                      </div>

                      <p className="text-xs text-gray-400 font-mono mb-3 truncate">ID: {twin.avatar_id}</p>

                      <div className="flex gap-2">
                        {twin.status === 'complete' && (
                          <button
                            onClick={() => useForVideo(twin.avatar_id)}
                            className="flex-1 bg-indigo-600 text-white rounded-xl py-2 text-xs font-semibold flex items-center justify-center gap-1"
                            data-testid="use-twin-for-video-btn"
                          >
                            <Video className="h-3.5 w-3.5" /> Use for Video
                          </button>
                        )}
                        <button
                          onClick={() => checkStatus(twin.avatar_id)}
                          className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2 text-xs font-semibold flex items-center justify-center gap-1"
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Refresh Status
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* CREATE FORM */}
        {view === 'create' && (
          <>
            {status === 'idle' || status === 'error' ? (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Twin Name
                    </label>
                    <input
                      type="text"
                      value={form.avatar_name}
                      onChange={e => setForm(p => ({ ...p, avatar_name: e.target.value }))}
                      placeholder="e.g. John Professional"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      data-testid="twin-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Training Footage URL
                    </label>
                    <input
                      type="url"
                      value={form.training_footage_url}
                      onChange={e => setForm(p => ({ ...p, training_footage_url: e.target.value }))}
                      placeholder="https://... (public MP4 URL, 2+ min, 720p+)"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      data-testid="twin-footage-url-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">Minimum 2 minutes, 720p or higher, well-lit</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Consent Video URL
                    </label>
                    <input
                      type="url"
                      value={form.video_consent_url}
                      onChange={e => setForm(p => ({ ...p, video_consent_url: e.target.value }))}
                      placeholder="https://... (video explicitly granting permission)"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      data-testid="twin-consent-url-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">A video where you clearly grant permission to create this avatar</p>
                    {/* Consent video explainer */}
                    <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
                      <p className="text-xs font-semibold text-indigo-800 mb-1">What is a Consent Video?</p>
                      <p className="text-xs text-indigo-700 mb-2">Record a short video (10–30 sec) of yourself clearly saying:</p>
                      <div className="bg-white border border-indigo-200 rounded-lg p-2 mb-2">
                        <p className="text-xs text-gray-700 italic">
                          "I, [your full name], give permission to create a Digital Twin AI avatar of me. I consent to using my likeness for AI video generation."
                        </p>
                      </div>
                      <p className="text-xs text-indigo-600">Host this video on Google Drive, Dropbox, or any public URL, then paste it above.</p>
                    </div>
                  </div>
                    <p className="text-xs text-red-500 bg-red-50 rounded-xl px-4 py-3">{errorMsg}</p>
                  )}

                  <button
                    onClick={submit}
                    disabled={!form.training_footage_url.trim() || !form.video_consent_url.trim() || !form.avatar_name.trim()}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-3.5 rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                    data-testid="twin-submit-btn"
                  >
                    <UserSquare className="h-4 w-4" /> Submit Digital Twin Creation
                  </button>
                </div>

                {/* Requirements */}
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Requirements</p>
                  <div className="space-y-2">
                    {[
                      ['Training footage', 'MP4 format, at least 2 minutes, 720p+ resolution, clear speech'],
                      ['Consent video', 'A video where you clearly state permission for avatar creation'],
                      ['Hosting', 'Both videos must be publicly accessible URLs (S3, GDrive, etc.)'],
                      ['Processing time', 'Training typically takes 30–60 minutes']
                    ].map(([k, v], i) => (
                      <div key={i} className="flex gap-2">
                        <div className="w-1 bg-indigo-300 rounded-full flex-shrink-0" />
                        <div>
                          <span className="text-xs font-semibold text-gray-700">{k}: </span>
                          <span className="text-xs text-gray-500">{v}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : status === 'submitting' ? (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                    <UserSquare className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Submitting Training Request</p>
                    <p className="text-xs text-gray-500">Uploading your footage metadata to HeyGen...</p>
                  </div>
                </div>
                <ProgressBar progress={progress} />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Submission Successful!</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Your Digital Twin is now being trained. This usually takes 30–60 minutes.
                  </p>
                  {newAvatarId && (
                    <p className="text-xs text-gray-400 mt-2 font-mono">Avatar ID: {newAvatarId}</p>
                  )}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                  <p className="text-xs text-blue-800">
                    Check "My Twins" tab to monitor training progress. Once complete, you can use it for video generation.
                  </p>
                </div>
                <button onClick={() => { setView('list'); reset(); }}
                  className="w-full border-2 border-gray-200 text-gray-700 font-semibold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  <Users className="h-4 w-4" /> View My Twins
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DigitalTwinPage;
